import { Link } from "react-router-dom";
import { LogIn, User as UserIcon } from "lucide-react";
import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import AuthModal from "./AuthModal";
import { useAuth } from "@/hooks/useAuth";
import { FooterLinkColumn, FooterLegalBar, type FooterLinkItem } from "./footer";
import { useRotatingTagline } from "@/hooks/useRotatingTagline";

const SiteFooter = () => {
  const [showAuth, setShowAuth] = useState(false);
  const { user, signOut } = useAuth();
  // Shares state with the hero — both surfaces show the same line and
  // advance together every few seconds.
  const tagline = useRotatingTagline("footer");

  const year = new Date().getFullYear();

  // Order matters: FooterLinkColumn renders into a 2-col grid, so this
  // sequence reads row-by-row as Directory / Compare / Protocols on top,
  // Quests / Learn / Workspace on the bottom.
  const exploreLinks: FooterLinkItem[] = [
    { label: "Directory", to: "/directory" },
    { label: "Hub", to: "/hub" },
    { label: "Quests", to: "/quests" },
    { label: "Leaderboard", to: "/leaderboard" },
    { label: "Pricing", to: "/pricing" },
    { label: "Compare", to: "/?tab=compare" },
  ];

  const connectLinks: FooterLinkItem[] = [
    { label: "About", to: "/about" },
    { label: "Newsletter", href: "#newsletter" },
    {
      label: "auth",
      render: () =>
        user ? (
          <button
            onClick={signOut}
            className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors py-1 min-h-[24px]"
          >
            <UserIcon className="w-3 h-3" />
            Sign out
          </button>
        ) : (
          <button
            onClick={() => setShowAuth(true)}
            className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors py-1 min-h-[24px]"
          >
            <LogIn className="w-3 h-3" />
            Login
          </button>
        ),
    },
  ];

  return (
    <>
      {/* The "Continue your journey" rail used to live here above the
          footer. Removed — the same CTA is already surfaced in the navbar
          (see NavNextChip) so this strip was duplicate real-estate. */}
      <div className="mt-16">
        <footer className="border-t border-border bg-background">
        <div className="container mx-auto px-4 py-10">
          {/* Newsletter is now inlined in the FooterLegalBar below to keep
              the footer dense — this used to be a separate centered strip. */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
            {/* Brand */}
            <div className="col-span-2 md:col-span-1 space-y-3">
              <Link to="/" className="inline-block" aria-label="HackerLabs.ai home">
                <span className="text-lg font-mono font-extrabold">
                  <span className="text-foreground">Hacker</span>
                  <span className="text-primary">Labs</span>
                  <span className="text-muted-foreground">.ai</span>
                </span>
              </Link>
              <p className="text-xs font-mono text-muted-foreground leading-relaxed min-h-[1.25em]">
                <AnimatePresence mode="wait" initial={false}>
                  <motion.span
                    key={tagline}
                    initial={{ opacity: 0, y: 3 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -3 }}
                    transition={{ duration: 0.3 }}
                    className="inline-block"
                  >
                    {tagline}
                  </motion.span>
                </AnimatePresence>
              </p>
            </div>

            <FooterLinkColumn title="Explore" links={exploreLinks} columns={2} />
            <FooterLinkColumn title="Connect" links={connectLinks} />
          </div>

        </div>
        </footer>
      </div>
      <FooterLegalBar year={year} />
      <AuthModal open={showAuth} onClose={() => setShowAuth(false)} />
    </>
  );
};

export default SiteFooter;
